import Link from "@web-ui/adapters/link";
import { usePathname } from "@web-ui/adapters/navigation";
import {
    Menu,
    MenuButton,
    MenuItem,
    MenuItems,
    MenuSeparator,
} from "@headlessui/react";
import Icon from "@web-ui/components/Icon";
import Avatar from "@web-ui/components/Avatar";
import type { UserMenuItem } from "@web-ui/types/navigation";

type UserProps = {
    /** Shown as initials in the avatar circle (the template shows a photo). */
    name: string;
    /** A second line under the name in the menu (a role, an email). */
    detail?: string;
    /** A picture instead of the initials. */
    avatarSrc?: string | null;
    items: UserMenuItem[];
    onLogout: () => void;
    logoutLabel?: string;
};

/* The picture or the initials come from the kit's Avatar (owner 2026-10-13: one avatar everywhere). */
const User = ({ name, detail, avatarSrc, items, onLogout, logoutLabel = "Log out" }: UserProps) => {
    const pathname = usePathname();
    const isActive = (href: string) => pathname === href;

    return (
        <>
            <Menu className="group" as="div">
                <MenuButton
                    className="relative z-40 w-12 h-12 rounded-full overflow-hidden transition-colors after:absolute after:inset-[0.09375rem] after:border-[0.15625rem] after:border-b-surface2 after:rounded-full data-[hover]:bg-accent-stroke data-[active]:bg-accent-stroke"
                    aria-label={name}
                >
                    <Avatar className="m-1" name={name} src={avatarSrc} />
                </MenuButton>
                {/* Tixora's profile popover (HeroUI popover dialog): who is signed in, a separator, the
                    links as menu items, then Sign out as a full width outline button. styles/controls.css .menu */}
                <MenuItems
                    className="menu menu--dialog w-70 max-md:w-[calc(100vw-2rem)]"
                    anchor="bottom end"
                    transition
                >
                    <div className="flex items-center gap-3 min-w-0">
                        <Avatar name={name} src={avatarSrc} />
                        <div className="min-w-0">
                            <div className="truncate text-body-2 font-semibold text-t-primary">{name}</div>
                            {detail ? <div className="truncate text-caption text-t-tertiary">{detail}</div> : null}
                        </div>
                    </div>
                    {items.length ? <MenuSeparator className="menu__separator !w-full !mx-0 !my-3" /> : null}
                    <div className="flex flex-col gap-1">
                        {items.map((link, index) => (
                            <MenuItem key={index}>
                                <Link
                                    className="menu__item gap-2"
                                    href={link.href}
                                    aria-current={isActive(link.href) ? "page" : undefined}
                                >
                                    <Icon name={link.icon} />
                                    {link.title}
                                </Link>
                            </MenuItem>
                        ))}
                    </div>
                    <MenuItem>
                        <button
                            type="button"
                            className="button button--outline button--sm button--full-width mt-3 data-[focus]:shadow-[0_0_0_2px_var(--control-focus)]"
                            onClick={onLogout}
                        >
                            <Icon name="logout" />
                            {logoutLabel}
                        </button>
                    </MenuItem>
                </MenuItems>
            </Menu>
        </>
    );
};

export default User;
